import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { Search, TabBar, tabLinkClassName } from "@bfc-software/pallet";
import {
  LocalizationProvider,
  useCurrentLocalization,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";

import { useTaskFilters, validateTaskFiltersSearch } from "./-data/filters";
import { SelectorFilter } from "./-components/selector-filter";
import { ZoneFilter } from "./-components/zone-filter";
import { CustomerFilter } from "./-components/customer-filter";

/**
 * Outbound Tasks: the shell the two tabs share.
 *
 * The tabs are two routes rather than two panels, because they are two questions
 * with almost nothing in common behind them. "Task plans" is the manager's
 * release decision - what is queued up and the button that puts it on the floor.
 * "All tasks" is the floor as it stands, ignoring which wave anything came from.
 * They read different endpoints, hold different state, and fail independently;
 * the only thing they genuinely share is what this file draws.
 *
 * So this route owns the heading, the two search boxes and the tab bar, and
 * nothing else. The searches live here rather than in each tab because searching
 * for a task is the same narrowing in both, and duplicating them per tab is what
 * lets the two drift out of sync.
 *
 * They reach the tabs through this route's *search params* rather than through a
 * context: a filtered view is something a manager sends to someone else, and a
 * view that lives in memory is one they have to describe over the phone instead.
 * The layout owning the params is also what lets both tabs read them with the
 * same hook - `-data/filters.ts` - whichever one is mounted.
 *
 * A pathless `_layout` segment, so the tabs are `/tasks/plans` and `/tasks/all`
 * and neither inherits an extra level of URL for the privilege of sharing a
 * header.
 */
export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/tasks/_layout",
)({
  validateSearch: validateTaskFiltersSearch,
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  return (
    <LocalizationProvider
      lang={params.lang}
      namespace={TRANSLATION_NAMESPACE.outbound}
    >
      <TasksShell />
    </LocalizationProvider>
  );
}

function TasksShell() {
  const params = Route.useParams();
  const [t] = useCurrentLocalization();

  const { search, setSearch } = useTaskFilters();

  return (
    <div className="page-shell" data-test-role="outbound-tasks">
      <header className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">{t("tasks.heading")}</h1>
          <p className="page-subtitle">{t("tasks.subheading")}</p>
        </div>
        <div className="page-header-right">
          <div className="page-header-searches">
            {/* Two boxes rather than one that guesses — unchanged rationale,
                see tasks/_layout.tsx:59-62 */}
            <div className="flex-1 min-w-0">
              <Search
                aria-label={t("tasks.search.taskId.ariaLabel")}
                placeholder={t("tasks.search.taskId.placeholder")}
                value={search.taskQuery ?? ""}
                onChange={(value) => setSearch({ taskQuery: String(value) })}
                onClear={() => setSearch({ taskQuery: undefined })}
              />
            </div>
            <div className="flex-1 min-w-0">
              <Search
                aria-label={t("tasks.search.routeId.ariaLabel")}
                placeholder={t("tasks.search.routeId.placeholder")}
                value={search.routeQuery ?? ""}
                onChange={(value) => setSearch({ routeQuery: String(value) })}
                onClear={() => setSearch({ routeQuery: undefined })}
              />
            </div>
          </div>
        </div>
      </header>

      <div className="flex items-center border-b border-outline-variant">
        {/*
          Links rather than tab buttons: the two tabs are routes, so the browser's
          back button and a pasted URL both have to land where the tab does. The
          underline is still the selected state - react-aria's `Tabs` would have
          duplicated the routing it already has. `TabBar` (pallet) owns only the
          layout; each `Link` supplies its own active state, including the
          `aria-current="page"` TanStack Router already sets.
        */}
        <div className="flex-none mr-16">
          <TabBar aria-label={t("tasks.tabs.ariaLabel")}>
            <Link
              to="/$lang/orgs/$org/outbound/whs/$location/tasks/plans"
              params={params}
              // The filters survive the tab change, which is the point of having
              // them in the URL: the same narrowing asked of the other question.
              search={true}
              inactiveProps={{ className: tabLinkClassName(false) }}
              activeProps={{ className: tabLinkClassName(true) }}
              data-test-role="tab-plans"
            >
              {t("tasks.tabs.plans")}
            </Link>
            <Link
              to="/$lang/orgs/$org/outbound/whs/$location/tasks/all"
              params={params}
              search={true}
              inactiveProps={{ className: tabLinkClassName(false) }}
              activeProps={{ className: tabLinkClassName(true) }}
              data-test-role="tab-all"
            >
              {t("tasks.tabs.all")}
            </Link>
          </TabBar>
        </div>

        {/*
          One group, not separate children of the row: a second filter added beside
          the first would otherwise be positioned on its own rather than sitting
          next to it. Both controls
          are the same kind of thing - a shared narrowing that belongs to
          neither tab - so they read as a pair on the right of the tabs.
          */}

        {/*
          No `grid` and no `gap` anywhere under the outbound tasks route tree: spacing is flex
          plus margins (`ml-*` on a following sibling, `mt-*` in a column), so what
          sits next to what is readable at the element that wears it. The filters
          share the tab row: the group is capped at 720px, never wraps, and its
          fields shrink (`flex-1 min-w-0`) before anything drops to a second row.
        */}
        <div
          className="flex flex-1 min-w-0 items-center ml-auto"
          // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
          style={{ maxWidth: 720 }}
        >
          {/*
            Shared by both tabs, for the reason `SelectorFilter` beside it is:
            `zones` is a URL param `useTaskFilters` owns, and a control inside
            one tab would stop narrowing the moment the other tab was open.

            It narrows every read on this screen on the wire - `zoneExtIds` on
            the plan listing, the activation listing and the planned-task
            listing alike - to rows whose picks were snapshotted into one of
            the chosen zones. All four request builders carry it - and
            `customerExtIds` beside it - so no surface here is left unnarrowed.
          */}
          <ZoneFilter />

          {/*
            Shared by both tabs, for the reason `ZoneFilter` above is: `customers`
            is a URL param `useTaskFilters` owns, and `customerExtIds` rides the
            same request builders as `zoneExtIds`.
          */}
          <CustomerFilter />

          {/*
            Shared by both tabs, same as the searches above: `selector` is a URL
            param `useTaskFilters` owns, not either tab's own state. It names a
            person, and it narrows both tabs on the wire - the same
            `selectorAppUserExtIds` param on each listing. The tasks nobody
            holds are the All tasks tab's `Unassigned` chip rather than a value
            of this box.
          */}
          <SelectorFilter />
        </div>
      </div>

      <Outlet />
    </div>
  );
}
